import { GlButton, GlCollapse, GlIcon } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { clearDraft } from '~/lib/utils/autosave';
import { s__ } from '~/locale';
import glFeatureFlagMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import createNoteMutation from '~/work_items/graphql/notes/create_work_item_note.mutation.graphql';
import toggleResolveDiscussionMutation from '~/work_items/graphql/notes/toggle_work_item_note_resolve_discussion.mutation.graphql';
import { ANSWER_ID_CUSTOM, PARSE_STATUS_OK, QUESTION_TYPE_CLOSED } from '../../constants';
import { buildDuoAnswer, buildDuoAnswers, parseDuoAnswer, parseDuoAnswerLabels, parseDuoQuestion, stripDuoAnswerMarker } from '../../utils/duo_question';
import { resolveDuoQuestionDecision } from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/work_items/utils/duo_question_decision.js?vue3';
import DuoCustomAnswerInput from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/work_items/components/notes/duo_custom_answer_input.vue?vue3';
import DuoMultiSelectOptions from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/work_items/components/notes/duo_multi_select_options.vue?vue3';
import DuoSingleSelectOptions from '/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/work_items/components/notes/duo_single_select_options.vue?vue3';
/**
 * Renders the clarifying question a workplan flow attached to its comment, so a
 * choice can be made in the discussion instead of by typing prose back at the agent.
 *
 * Answering posts the choice as a reply and resolves the thread. Resolving is the
 * part that matters: `Discussions::ResolveService` publishes
 * `WorkItems::DiscussionResolvedEvent`, and once every question the run posted is
 * resolved, `ContinueWorkplanAfterDiscussionsResolvedWorker` resumes the paused
 * workflow with the replies.
 *
 * An answer can also be written out when no option fits. That reply carries
 * `ANSWER_ID_CUSTOM`, so a decision log can tell a typed answer from a picked one.
 */ export default {
    name: 'DuoQuestionNote',
    components: {
        GlButton,
        GlCollapse,
        GlIcon,
        DuoCustomAnswerInput,
        DuoMultiSelectOptions,
        DuoSingleSelectOptions
    },
    mixins: [
        glFeatureFlagMixin()
    ],
    props: {
        note: {
            type: Object,
            required: true
        },
        workItemId: {
            type: String,
            required: true
        },
        fullPath: {
            type: String,
            required: true
        },
        workItemIid: {
            type: String,
            required: true
        },
        discussionId: {
            type: String,
            required: true
        },
        replies: {
            type: Array,
            required: true
        },
        isDiscussionResolved: {
            type: Boolean,
            required: true
        },
        isDiscussionResolvable: {
            type: Boolean,
            required: true
        },
        canReply: {
            type: Boolean,
            required: true
        }
    },
    emits: [
        'duo-question-answered',
        'error'
    ],
    data () {
        return {
            submittingId: null,
            answeredId: null,
            submittedCustomAnswer: '',
            selectedIds: [],
            customAnswer: '',
            submittedLabels: [],
            customAnswerResets: 0,
            showOptions: false
        };
    },
    computed: {
        duoWorkplanAsyncFlowEnabled () {
            return Boolean(this.glFeatures?.duoWorkplanAsyncFlow);
        },
        question () {
            if (!this.duoWorkplanAsyncFlowEnabled) return null;
            const result = parseDuoQuestion(this.note.body);
            if (result.status !== PARSE_STATUS_OK) return null;
            // An open question has nothing to pick from, so the prose above is already the
            // whole interaction.
            const { question } = result;
            return question.type === QUESTION_TYPE_CLOSED ? question : null;
        },
        options () {
            return this.question?.options ?? [];
        },
        // Read back so the answer survives page reloads.
        // Once the thread is resolved, the latest marker wins
        // since anyone can reply but only some can resolve the
        // thread and mark answer as accepted.
        answerReply () {
            if (!this.isDiscussionResolved) return undefined;
            return this.replies.findLast((reply)=>parseDuoAnswer(reply.body));
        },
        // Falls back to this session's answer, before the reply reaches the Apollo cache.
        answerId () {
            return this.answeredId ?? parseDuoAnswer(this.answerReply?.body);
        },
        chosenOption () {
            return this.options.find((option)=>option.id === this.answerId) ?? null;
        },
        customAnswerText () {
            if (this.answerId !== ANSWER_ID_CUSTOM) return '';
            return this.submittedCustomAnswer || stripDuoAnswerMarker(this.answerReply?.body);
        },
        isMultiple () {
            return Boolean(this.question?.multiple);
        },
        answeredLabels () {
            if (this.isMultiple) {
                if (this.submittedLabels.length) return this.submittedLabels;
                return this.answerReply ? parseDuoAnswerLabels(this.answerReply.body) : [];
            }
            if (this.chosenOption) return [
                this.chosenOption.label
            ];
            return this.customAnswerText ? [
                this.customAnswerText
            ] : [];
        },
        isAnswered () {
            return this.answeredLabels.length > 0;
        },
        isResolvedWithoutAnswer () {
            return this.isDiscussionResolved && !this.isAnswered;
        },
        isResolvedWithAnswer () {
            return this.isDiscussionResolved && this.isAnswered;
        },
        unchosenOptions () {
            const chosenIds = this.answerId?.split(',') ?? [];
            return this.options.filter((option)=>!chosenIds.includes(option.id));
        },
        displayedOptions () {
            return this.isAnswered ? this.unchosenOptions : this.options;
        },
        areOptionsCollapsible () {
            return this.isResolvedWithoutAnswer || this.isResolvedWithAnswer && this.unchosenOptions.length > 0;
        },
        areOptionsVisible () {
            return !this.areOptionsCollapsible || this.showOptions;
        },
        optionsToggleLabel () {
            if (this.isAnswered) {
                return this.showOptions ? s__('WorkItemDuoQuestion|Hide other suggested answers') : s__('WorkItemDuoQuestion|Show other suggested answers');
            }
            return this.showOptions ? s__('WorkItemDuoQuestion|Hide suggested answers') : s__('WorkItemDuoQuestion|Show suggested answers');
        },
        selectedOptions () {
            const picked = this.options.filter((option)=>this.selectedIds.includes(option.id));
            return this.customAnswer ? [
                ...picked,
                {
                    id: ANSWER_ID_CUSTOM,
                    label: this.customAnswer
                }
            ] : picked;
        },
        canSubmitSelection () {
            return this.selectedOptions.length > 0 && !this.isSubmitting;
        },
        isSubmitting () {
            return Boolean(this.submittingId);
        },
        // Answering posts a note, so a user who cannot comment is offered nothing to
        // click: the options stay visible, since they record what the agent weighed.
        isDisabled () {
            return this.isSubmitting || !this.canReply || this.areOptionsCollapsible;
        },
        isSubmittingCustomAnswer () {
            return this.submittingId === ANSWER_ID_CUSTOM;
        },
        customResponseDraftKey () {
            return `${this.discussionId}-duo-answer`;
        },
        // Unique per card, since a work item can show several question threads at once.
        customAnswerInputId () {
            return `duo-custom-answer-${this.note.id}`;
        },
        customAnswerInputKey () {
            return `${this.customAnswerInputId}-${this.customAnswerResets}`;
        },
        optionsId () {
            return `duo-question-options-${this.note.id}`;
        }
    },
    methods: {
        answer (option) {
            return this.postAnswer({
                id: option.id,
                body: buildDuoAnswer(option)
            });
        },
        submitSelection () {
            const { selectedOptions } = this;
            return this.postAnswer({
                id: selectedOptions.map(({ id })=>id).join(','),
                body: buildDuoAnswers(selectedOptions),
                onAnswered: ()=>{
                    this.submittedLabels = selectedOptions.map(({ label })=>label);
                    clearDraft(this.customResponseDraftKey);
                }
            });
        },
        submitCustomAnswer (label) {
            return this.postAnswer({
                id: ANSWER_ID_CUSTOM,
                body: buildDuoAnswer({
                    id: ANSWER_ID_CUSTOM,
                    label
                }),
                onAnswered: ()=>{
                    this.submittedCustomAnswer = label;
                    clearDraft(this.customResponseDraftKey);
                }
            });
        },
        async postAnswer ({ id, body, onAnswered }) {
            this.submittingId = id;
            try {
                const { data } = await this.$apollo.mutate({
                    mutation: createNoteMutation,
                    variables: {
                        input: {
                            noteableId: this.workItemId,
                            discussionId: this.discussionId,
                            body,
                            internal: this.note.internal
                        }
                    }
                });
                if (data?.createNote?.errors?.length) {
                    throw new Error(data.createNote.errors.join(', '));
                }
                // Answering and resolving happen together, so a user who cannot resolve only
                // gets the reply posted. Nothing is settled, and the options remain clickable.
                if (!this.isDiscussionResolvable) {
                    this.clearSelection();
                    return;
                }
                this.answeredId = id;
                onAnswered?.();
                if (await this.resolveThread()) {
                    resolveDuoQuestionDecision({
                        apolloClient: this.$apollo.getClient(),
                        fullPath: this.fullPath,
                        workItemIid: this.workItemIid,
                        discussionId: this.discussionId,
                        questionNote: this.note,
                        answerNote: {
                            id: data.createNote.note.id,
                            body
                        }
                    });
                }
                this.$emit('duo-question-answered');
            } catch (error) {
                this.$emit('error', s__('WorkItemDuoQuestion|Something went wrong while sending your answer. Please try again.'));
                Sentry.captureException(error);
            } finally{
                this.submittingId = null;
            }
        },
        clearSelection () {
            this.selectedIds = [];
            clearDraft(this.customResponseDraftKey);
            this.customAnswerResets += 1;
        },
        // A failure here must not read as the answer failing, since the reply is already
        // posted. It does need saying, though: resolving is what signals the question is
        // settled, so an unresolved thread leaves the run waiting.
        async resolveThread () {
            if (this.isDiscussionResolved) return false;
            try {
                const { data } = await this.$apollo.mutate({
                    mutation: toggleResolveDiscussionMutation,
                    variables: {
                        id: this.discussionId,
                        resolve: true
                    }
                });
                const errors = data?.discussionToggleResolve?.errors;
                if (errors?.length) throw new Error(errors.join(', '));
                return true;
            } catch (error) {
                this.$emit('error', s__('WorkItemDuoQuestion|Your answer was posted, but the thread could not be resolved. Resolve it manually so GitLab Duo can continue.'));
                Sentry.captureException(error);
                return false;
            }
        }
    }
};
